import { ResultExplain } from "@/components/ResultExplain";
import { t } from "@/lib/i18n";
import { mediaUrl } from "@/lib/api";

export const Bar = ({ value, testid }) => (
  <div className="h-2 bg-teal-soft rounded-full overflow-hidden">
    <div className="h-2 bg-teal rounded-full transition-[width] duration-700" style={{ width: `${Math.max(value, 3)}%` }} data-testid={testid} />
  </div>
);

const REQ_ES = { training: "capacitación", screening: "verificación de antecedentes", membership: "membresía", approval: "aprobación del liderazgo" };

export const ResultView = ({ v, reference, lang = "en" }) => {
  const primary = reference?.disc?.find((d) => d.key === v.disc_primary);
  const discName = (k) => reference?.disc?.find((d) => d.key === k)?.name;
  return (
    <div className="space-y-6">
      {v.faith_note && <p className="card-soft p-6 text-muted-ink leading-relaxed" data-testid="faith-note">{v.faith_note}</p>}
      {v.matches.length > 0 && <section className="card-soft p-8 md:p-10">
        <p className="eyebrow">{t(lang, "bestFit")}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {v.matches.map((m, i) => (
            <div key={m.team_id} className={`match-card rounded-2xl p-6 lift overflow-hidden ${i === 0 ? "bg-navy text-white lg:col-span-2" : "bg-paper"}`} data-testid={`match-${i}`}>
              {m.photo_file_id && <img src={mediaUrl(m.photo_file_id)} alt={m.name} className="-mx-6 -mt-6 mb-5 w-[calc(100%+3rem)] max-w-none h-36 object-cover" data-testid={`match-photo-${i}`} />}
              <div className="flex items-baseline justify-between">
                <span className={`font-mono-dt text-xs ${i === 0 ? "text-[#8FD3C6]" : "text-teal"}`}>#{i + 1}</span>
                <span className={`font-display text-2xl font-semibold ${i === 0 ? "text-[#8FD3C6]" : "text-teal"}`}>{m.score}%</span>
              </div>
              <h3 className="font-display text-xl font-semibold mt-3">{m.name}</h3>
              <p className={`text-sm mt-2 leading-relaxed ${i === 0 ? "text-white/75" : "text-muted-ink"}`}>{m.description}</p>
              {m.why_gifts?.length > 0 && (
                <p className={`text-xs mt-4 ${i === 0 ? "text-white/60" : "text-muted-ink"}`} data-testid={`match-gifts-${i}`}>{t(lang, "uses")} {m.why_gifts.join(", ")}</p>
              )}
              {m.why_disc?.length > 0 && (
                <p className={`text-xs mt-2 ${i === 0 ? "text-white/60" : "text-muted-ink"}`} data-testid={`match-disc-${i}`}>{lang === "es" ? "Fortalezas DISC:" : "DISC strengths:"} {m.why_disc.map((k) => `${k} · ${discName(k) || k}`).join(", ")}</p>
              )}
              {m.bridge && <p className={`text-xs mt-2 italic ${i === 0 ? "text-[#8FD3C6]" : "text-teal"}`} data-testid={`match-bridge-${i}`}>{m.bridge}</p>}
              {m.requirements?.length > 0 && (
                <p className={`text-xs mt-2 ${i === 0 ? "text-white/60" : "text-muted-ink"}`} data-testid={`match-req-${i}`}>{lang === "es" ? "Requiere:" : "Requires:"} {m.requirements.map((r) => (lang === "es" ? REQ_ES[r] || r : r)).join(", ")}</p>
              )}
            </div>
          ))}
        </div>
      </section>}

      <div className="grid lg:grid-cols-5 gap-6">
        <section className={`card-soft p-8 ${v.gift_scores.length ? "lg:col-span-2" : "lg:col-span-5"}`}>
          <p className="eyebrow">{t(lang, "discLabel")}</p>
          <p className="font-display text-teal text-6xl font-bold mt-4" data-testid="result-disc-profile">{v.disc_profile}</p>
          <p className="font-display text-navy text-lg font-semibold mt-2">{discName(v.disc_primary)}</p>
          {primary && <p className="text-muted-ink mt-3 leading-relaxed">{primary.summary}</p>}
          {primary && <p className="text-sm text-muted-ink mt-3"><strong className="text-navy">{t(lang, "watch")}</strong> {primary.watch}</p>}
          <div className="space-y-4 mt-8">
            {Object.entries(v.disc_scores).map(([k, s]) => (
              <div key={k}>
                <div className="flex justify-between text-sm mb-1"><span className="text-navy">{k} · {discName(k)}</span><span className="text-muted-ink">{s}%</span></div>
                <Bar value={s} testid={`disc-bar-${k}`} />
              </div>
            ))}
          </div>
        </section>

        {v.gift_scores.length > 0 && <section className="card-soft p-8 lg:col-span-3">
          <p className="eyebrow">{t(lang, "giftsLabel")}</p>
          <div className="grid sm:grid-cols-3 gap-4 mt-5">
            {v.gift_scores.slice(0, 3).map((g, i) => (
              <div key={g.key} className="bg-teal-soft rounded-xl p-5" data-testid={`top-gift-${i}`}>
                <p className="font-display text-navy font-semibold">{g.name}</p>
                <p className="text-xs text-muted-ink mt-2 leading-relaxed">{g.description}</p>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-8">
            {v.gift_scores.map((g) => (
              <div key={g.key}>
                <div className="flex justify-between text-sm mb-1"><span className="text-navy">{g.name}</span><span className="text-muted-ink">{g.score}%</span></div>
                <Bar value={g.score} />
              </div>
            ))}
          </div>
        </section>}
      </div>
      {reference && <ResultExplain v={v} reference={reference} lang={lang} />}
    </div>
  );
};
